import { gql } from "@apollo/client";
import {
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isValid } from "date-fns";
import { useMemo, useState } from "react";
import { RefreshIcon } from "../../components/Icons";
import { SafeLink } from "../../components/SafeLink";
import { useAuditLogsOptionsQuery } from "../../graphql/generated";
import { addOneDay } from "../../utils/time";
import styles from "./audit-logs-page.module.scss";

gql`
  query AuditLogsOptions {
    users {
      metadata {
        id
        name
        version
        displayName
      }
    }

    configurations {
      configurations {
        metadata {
          id
          name
          version
        }
      }
    }

    multiUserEnabled: featureGate(id: "multiuser")
  }
`;

interface AuditLogsOptionsProps {
  selectedConfig: string | null;
  setSelectedConfig: (config: string | null) => void;
  selectedUser: string | null;
  setSelectedUser: (user: string | null) => void;
  minDate: Date | null;
  setMinDate: (date: Date | null) => void;
  maxDate: Date | null;
  setMaxDate: (date: Date | null) => void;
  onRefreshClick: () => void;
}

export const AuditLogsOptions: React.FC<AuditLogsOptionsProps> = ({
  selectedConfig,
  selectedUser,
  minDate,
  maxDate,
  setSelectedConfig,
  setSelectedUser,
  setMinDate,
  setMaxDate,
  onRefreshClick,
}) => {
  const [configNames, setConfigNames] = useState<string[]>([]);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [multiUserEnabled, setMultiUserEnabled] = useState(false);

  useAuditLogsOptionsQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setUsernames(
        data.users.map((u) => u.metadata.displayName ?? u.metadata.name),
      );
      setConfigNames(
        data.configurations.configurations.map((c) => c.metadata.name),
      );
      setMultiUserEnabled(data.multiUserEnabled);
    },
  });

  function handleMinDateChange(date: Date | null) {
    if (date === null || isValid(date)) {
      setMinDate(date);
    }
  }

  function handleMaxDateChange(date: Date | null) {
    if (date === null || isValid(date)) {
      setMaxDate(date);
    }
  }

  const downloadHref = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (selectedConfig) {
      searchParams.set("configuration-name", selectedConfig);
    }

    if (selectedUser) {
      searchParams.set("user", selectedUser);
    }

    if (minDate) {
      searchParams.set("min-date", toAuditEventDateFormat(minDate));
    }

    if (maxDate) {
      const dayPlus1 = addOneDay(maxDate);
      if (dayPlus1 == null) {
        console.error(
          "Failed to add one day to max date, skipping search parameter for max-date",
        );
      } else {
        searchParams.set("max-date", toAuditEventDateFormat(dayPlus1));
      }
    }

    if (process.env.NODE_ENV === "development") {
      return `http://localhost:3001/v1/audit-events/download?${searchParams.toString()}`;
    }

    return `/v1/audit-events/download?${searchParams.toString()}`;
  }, [maxDate, minDate, selectedConfig, selectedUser]);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      marginBottom={2}
      spacing={2}
    >
      <Stack direction="row" spacing={2}>
        <FormControl
          classes={{
            root: styles.select,
          }}
        >
          <Autocomplete
            value={selectedConfig}
            onChange={(_e, newValue) => setSelectedConfig(newValue)}
            options={configNames}
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Filter by Configuration" />
            )}
          />
        </FormControl>

        {multiUserEnabled && (
          <FormControl
            classes={{
              root: styles.select,
            }}
          >
            <Autocomplete
              value={selectedUser}
              onChange={(_e, newValue) => setSelectedUser(newValue)}
              options={usernames}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Filter by User" />
              )}
            />
          </FormControl>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <DatePicker
          maxDate={maxDate ?? new Date()}
          slotProps={{
            textField: {
              size: "small",
            },
            actionBar: {
              actions: ["clear"],
            },
          }}
          value={minDate}
          onChange={handleMinDateChange}
        />

        <Typography>To</Typography>
        <DatePicker
          minDate={minDate ?? undefined}
          maxDate={new Date()}
          slotProps={{
            textField: {
              size: "small",
            },

            actionBar: {
              actions: ["clear"],
            },
          }}
          value={maxDate}
          onChange={handleMaxDateChange}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <Button
          LinkComponent={SafeLink}
          size="large"
          variant="contained"
          href={downloadHref}
          classes={{
            root: styles.button,
          }}
        >
          Export CSV
        </Button>
        <IconButton onClick={onRefreshClick} color="primary">
          <RefreshIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

// toAuditEventDateFormat converts a Date object
// to the string of formatYYYYMMDDHHMMSS in UTC timezone.
export function toAuditEventDateFormat(date: Date): string {
  const utcDate = date.getUTCDate();
  const utcMonth = date.getUTCMonth();
  const utcFullYear = date.getUTCFullYear();
  const utcHours = date.getUTCHours();

  const utc = new Date(Date.UTC(utcFullYear, utcMonth, utcDate, utcHours));
  return format(utc, "yyyyMMddHHmmss");
}
